import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SurveysAPI } from '../../../API';
import { DashboardDetails } from '../DashboardDetails';
import { hasTestTarget, runSurveyTest, TestLinkMode } from '../testSurveyLink';
import { dotColor } from './MarketList';
import { leadingTestRows, marketLabels, statusLabel, unwrapSurvey } from './surveyMetrics';

type Props = {
  survey: any;
  // The parent's children, or [survey] on a child page.
  markets: any[];
  // false for sales: the tab is read-only, without Run Test / Copy Test Link.
  canTest: boolean;
  // Test-user total of the page's survey (all markets on a parent), for the tab badge.
  onAllCount?: (count: number | string) => void;
};

type Scope = {
  loading: boolean;
  // The survey's own row from getOneDetails: testurl / useUniqueLinks as the
  // backend's testSurvey reads them (getChildSurveys inherits them from the parent).
  survey?: any;
  // Test-user rows; "N+" when there were more than COUNT_MAX_PAGES pages of them.
  count?: number | string;
  error?: string;
};

type State = {
  // '' = All markets; otherwise a child survey id. A child page ignores it.
  selectedId: string;
  // Per scope id: the parent (all markets) and each child.
  scopes: Record<string, Scope>;
  // `${marketId}|${mode}` while a test link is being made.
  busy: string | null;
  // Bumped after a test run so the Test Users table re-reads.
  tableKey: number;
};

const COUNT_PAGE_SIZE = 200;
const COUNT_MAX_PAGES = 5;

const errorText = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;
const isTested = (count: number | string | undefined) => typeof count === 'string' || (typeof count === 'number' && count > 0);

// One scope's own survey row and test-user count. getOneDetails lists the
// admin-local Test rows first, so the count reads pages until that run ends.
export const loadTestScope = async (id: string): Promise<{ survey: any; count: number | string }> => {
  let survey: any = null;
  let count = 0;
  for (let page = 1; page <= COUNT_MAX_PAGES; page++) {
    const res: any = await SurveysAPI.getOneDetails(id, COUNT_PAGE_SIZE, page);
    if (page === 1) survey = unwrapSurvey(res);
    const step = leadingTestRows(res?.data?.users, COUNT_PAGE_SIZE);
    count += step.count;
    if (step.complete) return { survey, count };
  }
  return { survey, count: `${count}+` };
};

export class TestingPane extends React.Component<Props, State> {
  private _mounted = false;

  state: State = { selectedId: '', scopes: {}, busy: null, tableKey: 0 };

  componentDidMount() {
    this._mounted = true;
    this.scopeIds().forEach((id) => this.loadScope(id));
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  isChild = () => !!this.props.survey.parentid;

  scopeIds = (): string[] => {
    const { survey, markets } = this.props;
    return this.isChild() ? [String(survey.id)] : [String(survey.id), ...markets.map((m) => String(m.id))];
  };

  loadScope = (id: string) => {
    this.setState((s) => ({ scopes: { ...s.scopes, [id]: { ...s.scopes[id], loading: true, error: undefined } } }));
    loadTestScope(id)
      .then(({ survey, count }) => {
        if (!this._mounted) return;
        this.setState((s) => ({ scopes: { ...s.scopes, [id]: { loading: false, survey, count } } }));
        if (id === String(this.props.survey.id) && this.props.onAllCount) this.props.onAllCount(count);
      })
      .catch((e: any) => {
        if (!this._mounted) return;
        this.setState((s) => ({ scopes: { ...s.scopes, [id]: { loading: false, error: errorText(e, 'Failed to load test settings') } } }));
      });
  };

  // Same handler the header Test / Copy Test Link buttons used, with this market's row.
  runTest = async (market: any, mode: TestLinkMode) => {
    const row = this.state.scopes[String(market.id)]?.survey;
    if (!row || this.state.busy) return;
    this.setState({ busy: `${market.id}|${mode}` });
    try {
      await runSurveyTest(row, mode);
    } finally {
      if (this._mounted) {
        this.setState((s) => ({ busy: null, tableKey: s.tableKey + 1 }));
        // The run may have added a test-user row: recount the market and the total.
        const ids = this.isChild() ? [String(market.id)] : [String(this.props.survey.id), String(market.id)];
        ids.forEach((id) => this.loadScope(id));
      }
    }
  };

  renderTestUrl = (scope: Scope | undefined) => {
    if (!scope || (scope.loading && !scope.survey)) return <div className="small text-muted">Loading…</div>;
    if (scope.error) return <div className="small text-danger">{scope.error}</div>;
    const row = scope.survey;
    if (row?.useUniqueLinks) return <div className="small text-muted">Uses test unique links</div>;
    if (row?.testurl) return <div className="small text-muted text-break dx-test-url">{row.testurl}</div>;
    return <div className="small text-muted">No test URL</div>;
  };

  renderTestRow = (market: any, labels: Record<string, string>) => {
    const { canTest } = this.props;
    const { scopes, busy } = this.state;
    const scope = scopes[String(market.id)];
    const row = scope?.survey;
    const status = (row || market).surveyType;
    const enabled = !!row && hasTestTarget(row) && !busy;
    const noTarget = !!row && !hasTestTarget(row);
    const title = noTarget ? 'Add a Survey Test URL first' : undefined;
    return (
      <div className="dx-test-row" key={market.id} data-testid={`test-row-${market.id}`}>
        <div className="dx-test-market">
          <span className="dx-status-dot" style={{ background: dotColor(status) }} title={statusLabel(status)} />
          <strong>{labels[market.id]}</strong> <span className="text-muted">{market.country}</span>
          {scope?.count !== undefined && (isTested(scope.count)
            ? <span className="badge badge-success ml-2">tested</span>
            : <span className="badge badge-secondary ml-2">not tested</span>)}
          {this.renderTestUrl(scope)}
        </div>
        {canTest && (
          <div className="dx-test-actions">
            <button
              type="button"
              className="btn btn-outline-info btn-sm mr-1"
              disabled={!enabled}
              title={title}
              onClick={() => this.runTest(market, 'copy')}
            >
              <FontAwesomeIcon icon={['fas', 'copy']} className="mr-1" />
              Copy Test Link
            </button>
            <button
              type="button"
              className="btn btn-info btn-sm"
              disabled={!enabled}
              title={title}
              onClick={() => this.runTest(market, 'run')}
            >
              <FontAwesomeIcon icon={['fas', 'list']} className="mr-1" />
              Run Test
            </button>
          </div>
        )}
      </div>
    );
  };

  renderMarketTabs = (labels: Record<string, string>) => {
    const { survey, markets } = this.props;
    const { selectedId, scopes } = this.state;
    const tab = (id: string, label: string, count: number | string | undefined) => (
      <button
        key={id || 'all'}
        type="button"
        role="tab"
        aria-selected={selectedId === id}
        className={`dx-tab${selectedId === id ? ' active' : ''}`}
        onClick={() => this.setState({ selectedId: id })}
      >
        {label}
        {count !== undefined && <span className="dx-tab-count">{count}</span>}
      </button>
    );
    return (
      <div className="dx-tabs" role="tablist" aria-label="Market">
        {tab('', 'All markets', scopes[String(survey.id)]?.count)}
        {markets.map((m) => tab(String(m.id), labels[m.id], scopes[String(m.id)]?.count))}
      </div>
    );
  };

  render() {
    const { survey, markets } = this.props;
    const { selectedId, tableKey } = this.state;
    const isChild = this.isChild();
    const labels = marketLabels(isChild ? [survey] : markets);
    const selected = isChild ? null : markets.find((m) => String(m.id) === selectedId) || null;
    const rows = isChild ? [survey] : (selected ? [selected] : markets);
    // All markets: the parent's table with a Market column. One market (a child
    // page or a market sub-tab): that market's own table, no Market column.
    const tableId = selected ? String(selected.id) : String(survey.id);

    return (
      <>
        {!isChild && this.renderMarketTabs(labels)}
        <div className="dx-card mb-3" data-testid="run-tests">
          <h6 className="mb-3">Run tests</h6>
          {rows.length === 0
            ? <p className="text-muted mb-0">No markets yet.</p>
            : <div className="dx-test-list">{rows.map((m) => this.renderTestRow(m, labels))}</div>}
        </div>
        <DashboardDetails
          key={`${tableId}-${tableKey}`}
          id={tableId}
          inline
          sections={['testUsers']}
          markets={!isChild && !selected ? markets : undefined}
        />
      </>
    );
  }
}
